import React, { useState } from 'react';
import {
  Plus,
  Pin,
  Edit3,
  Trash2,
  Bell,
  Calendar,
  Loader2,
  AlertCircle,
  X,
  Save,
} from 'lucide-react';
import {
  useAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  useToggleAnnouncementPin,
} from '../../hooks/useProfessor';
import type { AnnouncementResponse, CreateAnnouncementDto } from '@/types/professor.types';
import { CuteConfirmModal } from '@/shared/components/CuteConfirmModal';

interface AnnouncementsTabProps {
  groupeId: string;
  moduleElementId: string;
  // Year being consulted; omitted = the live one
  academicYearId?: string;
}

const AnnouncementsTab: React.FC<AnnouncementsTabProps> = ({ groupeId, moduleElementId, academicYearId }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; id: string | null; title: string }>({
    isOpen: false,
    id: null,
    title: '',
  });
  const [newAnnouncement, setNewAnnouncement] = useState<CreateAnnouncementDto>({
    title: '',
    content: '',
    isPinned: false,
  });
  const [editForm, setEditForm] = useState<{ title: string; content: string }>({
    title: '',
    content: '',
  });

  // Queries and mutations
  const { data: announcements, isLoading, error, refetch } = useAnnouncements(groupeId, moduleElementId, academicYearId);
  const createMutation = useCreateAnnouncement();
  const updateMutation = useUpdateAnnouncement();
  const deleteMutation = useDeleteAnnouncement();
  const togglePinMutation = useToggleAnnouncementPin();

  // Create announcement
  const handleCreate = async () => {
    if (!newAnnouncement.title.trim() || !newAnnouncement.content.trim()) return;

    try {
      await createMutation.mutateAsync({
        groupeId,
        moduleElementId,
        dto: newAnnouncement,
      });
      setNewAnnouncement({ title: '', content: '', isPinned: false });
      setIsCreating(false);
      refetch();
    } catch (error) {
      console.error('Failed to create announcement:', error);
    }
  };

  // Start editing
  const handleStartEdit = (announcement: AnnouncementResponse) => {
    setEditingId(announcement.id);
    setEditForm({ title: announcement.title, content: announcement.content });
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editingId || !editForm.title.trim() || !editForm.content.trim()) return;

    try {
      await updateMutation.mutateAsync({
        announcementId: editingId,
        groupeId,
        moduleElementId,
        dto: { title: editForm.title, content: editForm.content },
      });
      setEditingId(null);
      setEditForm({ title: '', content: '' });
      refetch();
    } catch (error) {
      console.error('Failed to update announcement:', error);
    }
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ title: '', content: '' });
  };

  // Open delete confirmation
  const openDeleteConfirm = (announcement: AnnouncementResponse) => {
    setDeleteConfirm({ isOpen: true, id: announcement.id, title: announcement.title });
  };

  // Delete announcement
  const handleDelete = async () => {
    if (!deleteConfirm.id) return;

    try {
      await deleteMutation.mutateAsync({ announcementId: deleteConfirm.id, groupeId, moduleElementId });
      setDeleteConfirm({ isOpen: false, id: null, title: '' });
      refetch();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
    }
  };

  // Toggle pin
  const handleTogglePin = async (id: string) => {
    try {
      await togglePinMutation.mutateAsync({ announcementId: id, groupeId, moduleElementId });
      refetch();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
        <AlertCircle className="h-5 w-5" />
        <span>Erreur lors du chargement des annonces</span>
        <button onClick={() => refetch()} className="ml-auto text-sm underline">
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Delete Confirmation Modal */}
      <CuteConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Supprimer l'annonce ?"
        message="Cette action est irréversible. L'annonce sera définitivement supprimée."
        itemName={deleteConfirm.title}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, title: '' })}
      />

      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Annonces</h3>
            <p className="text-sm text-gray-500 mt-1">
              Communiquez avec vos étudiants
            </p>
          </div>
          {!isCreating && (
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Nouvelle annonce
            </button>
          )}
        </div>

        {/* Create form */}
        {isCreating && (
          <div className="p-4 bg-blue-50 border-b border-blue-100">
            <h4 className="font-medium text-gray-900 mb-4">Nouvelle annonce</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={newAnnouncement.title}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Titre de l'annonce"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contenu *</label>
                <textarea
                  value={newAnnouncement.content}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, content: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Contenu de l'annonce..."
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPinned"
                  checked={newAnnouncement.isPinned}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, isPinned: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isPinned" className="text-sm text-gray-700">
                  Épingler cette annonce (apparaîtra en premier)
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => {
                    setIsCreating(false);
                    setNewAnnouncement({ title: '', content: '', isPinned: false });
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!newAnnouncement.title.trim() || !newAnnouncement.content.trim() || createMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Publier
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Announcements list */}
        <div className="p-4">
          {(!announcements || announcements.length === 0) ? (
            <div className="text-center py-12">
              <Bell className="mx-auto h-12 w-12 text-gray-400" />
              <h4 className="mt-4 text-lg font-medium text-gray-900">Aucune annonce</h4>
              <p className="mt-2 text-sm text-gray-500">
                Créez votre première annonce pour informer vos étudiants
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className={`rounded-xl border shadow-sm overflow-hidden ${
                    announcement.isPinned ? 'border-blue-300 bg-blue-50/30' : 'border-gray-200 bg-white'
                  }`}
                >
                  {editingId === announcement.id ? (
                    // Edit mode
                    <div className="p-4">
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editForm.title}
                          onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Titre"
                        />
                        <textarea
                          value={editForm.content}
                          onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Contenu..."
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1"
                          >
                            <X className="h-4 w-4" />
                            Annuler
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            disabled={!editForm.title.trim() || !editForm.content.trim() || updateMutation.isPending}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-1"
                          >
                            {updateMutation.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                            Enregistrer
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // View mode
                    <div className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {announcement.isPinned && (
                            <Pin className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          )}
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-gray-900">{announcement.title}</h4>
                              {announcement.isPinned && (
                                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                  Épinglé
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDate(announcement.createdAt)}
                              {announcement.updatedAt !== announcement.createdAt && (
                                <span className="text-xs text-gray-400">(modifié)</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleTogglePin(announcement.id)}
                            disabled={togglePinMutation.isPending}
                            className={`p-1.5 rounded transition-colors ${
                              announcement.isPinned
                                ? 'text-blue-600 hover:bg-blue-100'
                                : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                            }`}
                            title={announcement.isPinned ? 'Désépingler' : 'Épingler'}
                          >
                            <Pin className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleStartEdit(announcement)}
                            className="p-1.5 text-gray-400 rounded hover:bg-gray-100 hover:text-gray-600 transition-colors"
                            title="Modifier"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm(announcement)}
                            disabled={deleteMutation.isPending}
                            className="p-1.5 text-gray-400 rounded hover:bg-red-50 hover:text-red-600 transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      <p className="mt-3 text-gray-600 whitespace-pre-wrap pl-8">
                        {announcement.content}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info message */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Les annonces sont visibles par tous les étudiants de ce cours.
            Les annonces épinglées apparaissent toujours en premier.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementsTab;
